import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { AutoScrollDirective } from '../../directives/auto-scroll.directive';
import { ChatInputRowComponent } from '../chat-input-row/chat-input-row.component';
import { LobbyFeedEventComponent } from '../lobby-feed-event/lobby-feed-event.component';
import { TeamSetupCtaComponent } from '../team-setup-cta/team-setup-cta.component';
import { ChallengeService } from '../../services/challenge.service';
import { ChatService } from '../../services/chat.service';
import { MobileScaleService } from '../../services/mobile-scale.service';
import { RegistryMigrationService } from '../../services/registry-migration.service';
import { createSnapTabStrip } from '../../utils/tab-indicator';
import { visibleInterval } from '../../utils/visible-interval';

type LobbyTab = 'all' | 'game' | 'chat';

@Component({
  selector: 'app-lobby-feed',
  imports: [
    AutoScrollDirective,
    ChatInputRowComponent,
    LobbyFeedEventComponent,
    TeamSetupCtaComponent,
  ],
  templateUrl: './lobby-feed.component.html',
  styleUrls: ['./lobby-feed.component.css', './lobby-feed.component.mobile.css'],
  host: { '(window:resize)': 'onWindowResize()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LobbyFeedComponent {
  readonly hasTeam = input<boolean | undefined>(undefined);

  readonly openOnboarding = output<void>();

  protected readonly chat = inject(ChatService);
  protected readonly mobile = inject(MobileScaleService);
  protected readonly challenge = inject(ChallengeService);
  protected readonly migration = inject(RegistryMigrationService);

  protected readonly tabs = [
    { id: 'all', label: 'All' },
    { id: 'game', label: 'Game' },
    { id: 'chat', label: 'Chat' },
  ] as const;
  activeLobbyTab = signal<LobbyTab>('all');

  readonly gameEventsLog = this.chat.lobbyGameEvents;
  readonly chatOnlyLog = computed(() =>
    this.chat.lobbyLogMessages().filter(
      (m) => m.type === 'YOU_CHAT' || m.type === 'OTHERS_CHAT'
    )
  );

  readonly completedBattleKeys = this.chat.completedBattleKeys;

  readonly nowMs = signal(Date.now());

  readonly snapContainer = viewChild<ElementRef<HTMLDivElement>>('snapContainer');
  readonly logTabRefs = viewChildren<ElementRef<HTMLButtonElement>>('logTab');
  readonly tabIndicator = viewChild<ElementRef<HTMLSpanElement>>('tabIndicator');

  private readonly snapTabs = createSnapTabStrip({
    tabIds: this.tabs.map((t) => t.id),
    activeTab: this.activeLobbyTab,
    snap: () => this.snapContainer()?.nativeElement,
    tabs: () => this.logTabRefs(),
    indicator: () => this.tabIndicator()?.nativeElement,
  });

  constructor() {
    inject(DestroyRef).onDestroy(visibleInterval(60_000, () => this.nowMs.set(Date.now())));
  }

  onWindowResize() {
    this.snapTabs.update();
  }

  setActiveLobbyTab(tab: LobbyTab) {
    this.snapTabs.setActiveTab(tab);
  }

  onSnapScroll(event: Event) {
    this.snapTabs.onScroll(event);
  }

  sendLobbyChatMessage(message: string) {
    this.chat.sendLobbyMessage(message);
  }
}
